import Form from 'Components/Forms/FormGenerator';
import FormViewer from 'Components/FormViewer';
import Icon from 'Components/Icon/index.js';
import { actions as deviceActions } from 'ducks/devices';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectEligibleNewParents } from 'selectors/devices';
import { serializeForm } from 'utils/forms/utils';
import validators from 'utils/forms/validators';
import sortDevices from 'utils/sortDevices.js';
import statusToColor from 'utils/statusToColor.js';

const childFieldName = id => `child_${id}`;

const getFullPath = (device, allDevices) => {
    let path = device.name;
    let parent;

    while (true) {
        parent = allDevices[device.parent_id];

        if (parent) {
            path = `${parent.name} > ${path}`;
            device = parent;
        } else {
            break;
        }
    }

    return path;
};

const getSecondaryText = device => {
    let text = device.equipment_name;

    const attributeToName = { ip: 'IP', mbus_id: 'MBus', modbus_id: 'Modbus', one_wire_id: 'OneWire' };

    for (const [key, label] of Object.entries(attributeToName)) {
        const value = device.extra[key];
        if (value) {
            text = `${label} ${value} (${text})`;
            break;
        }
    }

    return (
        <>
            <Icon color={statusToColor(device.status)} icon={['fas', 'circle']} size="xs" style={{ marginRight: 5 }}/>
            <b style={{ fontSize: 10, color: 'gray' }}>{text}</b>
        </>
    );
};

const Component = ({ device, childDevices, onSave }) => {
    const submitting = useSelector(state => state.devices.submitting);
    const allDevices = useSelector(state => state.devices.models);
    const eligibleParentsSelector = useMemo(() => selectEligibleNewParents(device.id), [device.id]);
    const eligibleParents = useSelector(eligibleParentsSelector);
    const dispatch = useDispatch();

    if (!eligibleParents.length) {
        return (
            <FormViewer label="Move children">
                <div>
                    No eligible new parent found in the same site with output protocol &quot;{device.out_protocol_name}&quot;.
                </div>
            </FormViewer>
        );
    }

    let children = childDevices.sort(sortDevices).map(child => ({ id: child.id, name: <><span>{child.name}</span><br/><span>{getSecondaryText(child)}</span></> }));
    let parentOptions = sortBy(eligibleParents.map(parent => ({ id: parent.id, name: getFullPath(parent, allDevices) })), 'name');

    const formSchema = [
        {
            type: 'select',
            label: 'New parent\x2a',
            name: 'parent_id',
            formData: parentOptions,
            validate: [validators.required]
        },
        {
            type: 'label',
            label: 'Children to move\x2a',
            name: '__children_label'
        },
        ...children.map(child => ({
            type: 'checkBox',
            label: child.name,
            name: childFieldName(child.id)
        }))
    ];

    const formInitialValues = children.reduce(
        (acc, child) => {
            acc[childFieldName(child.id)] = false;
            return acc;
        },
        { parent_id: '' }
    );

    const onSubmit = values => {
        const data = serializeForm(values, formSchema);
        const ids = children
            .filter(child => data[childFieldName(child.id)])
            .map(child => child.id);

        if (ids.length && data.parent_id) {
            dispatch(deviceActions.moveChildren(
                { ids, parentId: data.parent_id, siteId: device.site_id },
                onSave
            ));
        }
    };

    return (
        <FormViewer label="Move children">
            <Form
                formInitialValues={formInitialValues}
                formSchema={formSchema}
                isSubmitting={submitting}
                onSubmit={onSubmit}
            />
        </FormViewer>
    );
};

Component.propTypes = {
    device: PropTypes.object.isRequired,
    childDevices: PropTypes.array.isRequired,
    onSave: PropTypes.func
};

export default Component;
